<script>
    import { fade } from 'svelte/transition';
    import { push } from 'svelte-spa-router';
    import { _ } from "svelte-i18n";
    import { onMount } from 'svelte';
    import fetchData from '../api.js';
    import UserStore from '../stores/user';
    import Header from '../components/Header.svelte';
    import TopInfo from '../components/TopInfo.svelte';
    import Preloader from '../components/Preloader.svelte';
    import Chart from '../components/Chart.svelte';
    import { updateUrlParam } from '../base/url.js';

    const MAX_REPORT_PAGES = 25;
    const REPORT_LIMIT = 200;

    let isLoading = false;
    let showPartialNotice = false;

    let dateFrom = '';
    let dateTo = '';
    let selectedCompany = '';

    let allOrders = [];
    let reportOrders = [];
    let availableCompanies = [];

    let kpis = {
        total_orders: 0,
        issued_shipments: 0,
        open_orders: 0,
        pickup_orders: 0,
        door_orders: 0,
    };

    let dailyRows = [];

    let dailyChartData = {
        labels: [],
        datasets: [{
            label: '',
            data: [],
            borderWidth: 1,
            backgroundColor: '#1d4ed8',
        }],
    };

    let statusChartData = {
        labels: [],
        datasets: [{
            label: '',
            data: [],
            borderWidth: 1,
            backgroundColor: ['#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#7c3aed', '#14b8a6'],
        }],
    };

    let companyChartData = {
        labels: [],
        datasets: [{
            label: '',
            data: [],
            borderWidth: 1,
            backgroundColor: '#334155',
        }],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    precision: 0,
                },
            },
        },
    };

    function toInputDate(dateObj) {
        const year = dateObj.getFullYear();
        const month = `${dateObj.getMonth() + 1}`.padStart(2, '0');
        const day = `${dateObj.getDate()}`.padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    function setDatePreset(daysBack) {
        const today = new Date();
        const start = new Date();
        start.setDate(today.getDate() - daysBack);

        dateTo = toInputDate(today);
        dateFrom = toInputDate(start);
    }

    function getHashParams() {
        const url = new URL(window.location);
        return new URLSearchParams(url.hash.split('?')[1]);
    }

    function getOrderDate(order) {
        if (!order || !order.date) {
            return '';
        }
        return String(order.date).split(' ')[0] || '';
    }

    function getOrderCompanyId(order) {
        return String(
            order?.shipping_push_supplier
            ?? order?.shipping_supplier
            ?? order?.shipping_company_id
            ?? ''
        );
    }

    function getOrderCompanyName(order, companyId) {
        if (typeof platforms !== 'undefined' && companyId && platforms[companyId]?.name) {
            return platforms[companyId].name;
        }

        return order?.shipping_company
            || order?.shipping_company_name
            || order?.supplier_name
            || companyId
            || 'N/A';
    }

    function getStatusLabel(order) {
        const shipmentStatus = order?.shipment_status?.status;
        if (shipmentStatus) {
            return String(shipmentStatus).trim();
        }

        if (order?.order_status) {
            return String(order.order_status).trim();
        }

        return order?.shipment_id ? 'Issued' : 'Open';
    }

    function isPickupOrder(order) {
        return order?.ncode !== null && order?.ncode !== '' && typeof order?.ncode !== 'undefined';
    }

    function getOrderNumber(order) {
        return order?.order_name
            || order?.order_number
            || order?.external_id
            || order?.order_id
            || '';
    }

    function getTrackingNumber(order) {
        return order?.shipment_id
            || order?.tracking_number
            || order?.tracking
            || order?.track_number
            || '';
    }

    function escapeCsvValue(value) {
        const cleanValue = value === null || typeof value === 'undefined' ? '' : String(value);
        return `"${cleanValue.replace(/"/g, '""')}"`;
    }

    function downloadCsv(csvContent, fileName) {
        const csvBlob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-8;' });
        const downloadUrl = URL.createObjectURL(csvBlob);
        const anchor = document.createElement('a');
        anchor.href = downloadUrl;
        anchor.download = fileName;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(downloadUrl);
    }

    function exportDetailedOrders() {
        if (!reportOrders.length) {
            alert($_('reports.no_data'));
            return;
        }

        const headers = [
            $_('reports.export_date'),
            $_('reports.export_order_number'),
            $_('reports.export_order_id'),
            $_('reports.export_tracking_number'),
            $_('reports.export_company'),
            $_('reports.export_customer'),
            $_('reports.export_status'),
        ];

        const rows = reportOrders.map((order) => {
            const companyId = getOrderCompanyId(order);
            return [
                getOrderDate(order),
                getOrderNumber(order),
                order?.order_id || '',
                getTrackingNumber(order),
                getOrderCompanyName(order, companyId),
                order?.shipping_fullname || order?.customer || order?.customer_name || '',
                getStatusLabel(order),
            ];
        });

        const csvContent = [headers, ...rows]
            .map((row) => row.map(escapeCsvValue).join(','))
            .join('\n');

        const fileName = `reports_orders_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
        downloadCsv(csvContent, fileName);
    }

    function applyCompanyFilter(orders) {
        if (!selectedCompany) {
            return orders;
        }

        return orders.filter((order) => getOrderCompanyId(order) === selectedCompany);
    }

    function buildCompanyOptions(orders) {
        const map = new Map();

        orders.forEach((order) => {
            const companyId = getOrderCompanyId(order);
            const companyName = getOrderCompanyName(order, companyId);

            if (!companyId || map.has(companyId)) {
                return;
            }

            map.set(companyId, {
                id: companyId,
                name: companyName,
            });
        });

        availableCompanies = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    }

    function buildReportModel(rawOrders) {
        const orders = applyCompanyFilter(rawOrders);
        reportOrders = [...orders];

        const dayMap = new Map();
        const statusMap = new Map();
        const companyMap = new Map();

        let issuedShipments = 0;
        let pickupOrders = 0;

        orders.forEach((order) => {
            const day = getOrderDate(order);
            if (!day) {
                return;
            }

            const isPickup = isPickupOrder(order);
            const hasShipment = order?.shipment_id !== null && typeof order?.shipment_id !== 'undefined';
            const status = getStatusLabel(order);

            const currentDay = dayMap.get(day) || {
                date: day,
                orders: 0,
                shipments: 0,
                pickup: 0,
                door: 0,
            };

            currentDay.orders += 1;
            currentDay.shipments += hasShipment ? 1 : 0;
            currentDay.pickup += isPickup ? 1 : 0;
            currentDay.door += isPickup ? 0 : 1;
            dayMap.set(day, currentDay);

            if (hasShipment) {
                issuedShipments += 1;
            }
            if (isPickup) {
                pickupOrders += 1;
            }

            statusMap.set(status, (statusMap.get(status) || 0) + 1);

            const companyId = getOrderCompanyId(order);
            const companyName = getOrderCompanyName(order, companyId);
            companyMap.set(companyName, (companyMap.get(companyName) || 0) + 1);
        });

        const sortedByDay = Array.from(dayMap.values()).sort((a, b) => a.date.localeCompare(b.date));

        dailyRows = [...sortedByDay].reverse();

        const last30Days = sortedByDay.slice(-30);

        dailyChartData = {
            labels: last30Days.map((row) => row.date),
            datasets: [{
                label: $_('reports.shipments'),
                data: last30Days.map((row) => row.shipments),
                borderWidth: 1,
                backgroundColor: '#1d4ed8',
            }],
        };

        const topStatuses = Array.from(statusMap.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 6);

        statusChartData = {
            labels: topStatuses.map((row) => row[0]),
            datasets: [{
                label: $_('reports.orders'),
                data: topStatuses.map((row) => row[1]),
                borderWidth: 1,
                backgroundColor: ['#0ea5e9', '#22c55e', '#f59e0b', '#ef4444', '#7c3aed', '#14b8a6'],
            }],
        };

        const topCompanies = Array.from(companyMap.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8);

        companyChartData = {
            labels: topCompanies.map((row) => row[0]),
            datasets: [{
                label: $_('reports.orders'),
                data: topCompanies.map((row) => row[1]),
                borderWidth: 1,
                backgroundColor: '#334155',
            }],
        };

        kpis = {
            total_orders: orders.length,
            issued_shipments: issuedShipments,
            open_orders: Math.max(orders.length - issuedShipments, 0),
            pickup_orders: pickupOrders,
            door_orders: Math.max(orders.length - pickupOrders, 0),
        };
    }

    async function fetchOrders() {
        const filters = {};

        if (dateFrom) {
            filters.date_from = dateFrom;
        }

        if (dateTo) {
            filters.date_to = dateTo;
        }

        filters.date_sort = 'desc';

        const basePostData = {
            action: 'crm_get_shipments',
            token: $UserStore.data,
            filters,
            limit: REPORT_LIMIT,
        };

        const firstPageResponse = await fetchData({ ...basePostData, page: 1 });
        const firstPageData = firstPageResponse?.data || [];
        const totalCount = Number(firstPageResponse?.count || firstPageData.length || 0);
        const totalPages = Math.max(1, Math.ceil(totalCount / REPORT_LIMIT));
        const pageLimit = Math.min(totalPages, MAX_REPORT_PAGES);

        showPartialNotice = totalPages > MAX_REPORT_PAGES;

        let merged = [...firstPageData];

        for (let page = 2; page <= pageLimit; page += 1) {
            const response = await fetchData({ ...basePostData, page });
            const pageData = response?.data || [];
            merged = merged.concat(pageData);
        }

        return merged;
    }

    async function loadReports() {
        if (!$UserStore || !$UserStore.data) {
            push('/login');
            return;
        }

        isLoading = true;

        try {
            allOrders = await fetchOrders();
            buildCompanyOptions(allOrders);
            buildReportModel(allOrders);
        } finally {
            isLoading = false;
        }
    }

    async function applyFilters() {
        updateUrlParam({
            date_from: dateFrom,
            date_to: dateTo,
            company: selectedCompany,
        });

        await loadReports();
    }

    async function clearFilters() {
        selectedCompany = '';
        setDatePreset(29);

        updateUrlParam({
            date_from: dateFrom,
            date_to: dateTo,
            company: '',
        });

        await loadReports();
    }

    onMount(async () => {
        const params = getHashParams();
        setDatePreset(29);

        if (params.get('date_from')) {
            dateFrom = params.get('date_from');
        }

        if (params.get('date_to')) {
            dateTo = params.get('date_to');
        }

        if (params.get('company')) {
            selectedCompany = params.get('company');
        }

        await loadReports();
    });
</script>

<div class="page-container" transition:fade={{ delay: 250, duration: 300 }}>
    <Preloader {isLoading}/>
    <Header activeLink="reports" />

    <div class="page">
        <TopInfo />

        <div class="shipment">
            <div class="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div>
                    <h1 class="text-[20px] font-bold text-start">{$_('reports.title')}</h1>
                    <p class="text-[13px] text-slate-600">{$_('reports.subtitle')}</p>
                </div>
            </div>

            <div class="bg-white border border-slate-200 rounded-[8px] p-3 mb-4">
                <div class="flex flex-wrap gap-2 items-end">
                    <div class="input_con min-w-[160px]">
                        <label for="reports-date-from">{$_('reports.date_from')}</label>
                        <input id="reports-date-from" type="date" bind:value={dateFrom}>
                    </div>
                    <div class="input_con min-w-[160px]">
                        <label for="reports-date-to">{$_('reports.date_to')}</label>
                        <input id="reports-date-to" type="date" bind:value={dateTo}>
                    </div>
                    <div class="input_con min-w-[220px]">
                        <label for="reports-company">{$_('reports.company')}</label>
                        <select id="reports-company" bind:value={selectedCompany}>
                            <option value="">{$_('reports.all_companies')}</option>
                            {#each availableCompanies as company}
                                <option value={company.id}>{company.name}</option>
                            {/each}
                        </select>
                    </div>
                    <button class="create_button" type="button" on:click={() => { setDatePreset(6); applyFilters(); }}>{$_('reports.last_7')}</button>
                    <button class="create_button" type="button" on:click={() => { setDatePreset(29); applyFilters(); }}>{$_('reports.last_30')}</button>
                    <button class="create_button" type="button" on:click={() => { setDatePreset(89); applyFilters(); }}>{$_('reports.last_90')}</button>
                    <button class="create_button" type="button" on:click={applyFilters}>{$_('reports.filter')}</button>
                    <button class="create_button" type="button" on:click={clearFilters}>{$_('reports.clear')}</button>
                    <button class="create_button" type="button" on:click={exportDetailedOrders}>{$_('reports.export_csv')}</button>
                </div>

                {#if showPartialNotice}
                    <div class="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 mt-3">
                        {$_('reports.partial_notice')}
                    </div>
                {/if}
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 mb-4">
                <div class="bg-white border border-slate-200 rounded-[8px] p-3">
                    <div class="text-[12px] text-slate-500">{$_('reports.total_orders')}</div>
                    <div class="text-[24px] font-bold">{kpis.total_orders}</div>
                </div>
                <div class="bg-white border border-slate-200 rounded-[8px] p-3">
                    <div class="text-[12px] text-slate-500">{$_('reports.issued_shipments')}</div>
                    <div class="text-[24px] font-bold">{kpis.issued_shipments}</div>
                </div>
                <div class="bg-white border border-slate-200 rounded-[8px] p-3">
                    <div class="text-[12px] text-slate-500">{$_('reports.open_orders')}</div>
                    <div class="text-[24px] font-bold">{kpis.open_orders}</div>
                </div>
                <div class="bg-white border border-slate-200 rounded-[8px] p-3">
                    <div class="text-[12px] text-slate-500">{$_('reports.pickup_orders')}</div>
                    <div class="text-[24px] font-bold">{kpis.pickup_orders}</div>
                </div>
                <div class="bg-white border border-slate-200 rounded-[8px] p-3">
                    <div class="text-[12px] text-slate-500">{$_('reports.door_orders')}</div>
                    <div class="text-[24px] font-bold">{kpis.door_orders}</div>
                </div>
            </div>

            <div class="grid grid-cols-1 xl:grid-cols-3 gap-3 mb-4">
                <div class="bg-white border border-slate-200 rounded-[8px] p-3 xl:col-span-2">
                    <h3 class="font-bold mb-2">{$_('reports.daily_shipments')}</h3>
                    <div class="h-[280px]">
                        <Chart type="bar" data={dailyChartData} options={chartOptions} />
                    </div>
                </div>
                <div class="bg-white border border-slate-200 rounded-[8px] p-3">
                    <h3 class="font-bold mb-2">{$_('reports.status_breakdown')}</h3>
                    <div class="h-[280px]">
                        <Chart type="bar" data={statusChartData} options={chartOptions} />
                    </div>
                </div>
            </div>

            <div class="bg-white border border-slate-200 rounded-[8px] p-3 mb-4">
                <h3 class="font-bold mb-2">{$_('reports.top_companies')}</h3>
                <div class="h-[240px]">
                    <Chart type="bar" data={companyChartData} options={chartOptions} />
                </div>
            </div>

            <div class="bg-white border border-slate-200 rounded-[8px] p-3 overflow-auto">
                <h3 class="font-bold mb-2">{$_('reports.daily_summary')}</h3>

                {#if dailyRows.length === 0}
                    <div class="text-[13px] text-slate-500">{$_('reports.no_data')}</div>
                {:else}
                    <table class="w-full text-[13px]">
                        <thead>
                            <tr class="text-start border-b border-slate-200">
                                <th class="py-2 text-start">{$_('reports.date')}</th>
                                <th class="py-2 text-start">{$_('reports.orders')}</th>
                                <th class="py-2 text-start">{$_('reports.shipments')}</th>
                                <th class="py-2 text-start">{$_('reports.pickup')}</th>
                                <th class="py-2 text-start">{$_('reports.door')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#each dailyRows as row}
                                <tr class="border-b border-slate-100">
                                    <td class="py-2">{row.date}</td>
                                    <td class="py-2">{row.orders}</td>
                                    <td class="py-2">{row.shipments}</td>
                                    <td class="py-2">{row.pickup}</td>
                                    <td class="py-2">{row.door}</td>
                                </tr>
                            {/each}
                        </tbody>
                    </table>
                {/if}
            </div>
        </div>
    </div>
</div>
